import { http, HttpResponse } from 'msw';

import { env } from '@/config/env';
import { axios } from '@/lib/axios';
import { networkDelay } from '@/test/mocks/utils';

import { GetQuizAttemptSummary } from '../types';

export const getQuizAttemptSummary = (
  quizId: string
): Promise<GetQuizAttemptSummary.Root> => {
  return axios.get('/assessment/v1/users/reports/get-quiz-attempt-summary', {
    params: {
      quizId,
    },
  });
};

// Deterministic mock data so the quiz summary modal can be reliably viewed
// in dev. Two attempts exercise the multi-attempt (compact) layout.
const buildAttempts = (): GetQuizAttemptSummary.Attempt[] => {
  const nowInSeconds = Math.floor(Date.now() / 1000);
  const oneDayInSeconds = 24 * 60 * 60;

  return [
    {
      attemptNumber: 1,
      date: (nowInSeconds - oneDayInSeconds).toString(),
      marksObtained: 70,
      coinsEarned: 10,
      accuracy: 70,
      proficiency: 0.7,
      speedPercentile: 72,
      timeSpentSec: 300,
      totalQuestions: 20,
      correctQuestions: 14,
      testType: 'QUIZ',
      sessionId: 'quiz-session-001',
    },
    {
      attemptNumber: 2,
      date: nowInSeconds.toString(),
      marksObtained: 90,
      coinsEarned: 15,
      accuracy: 90,
      proficiency: 0.9,
      speedPercentile: 88,
      timeSpentSec: 240,
      totalQuestions: 20,
      correctQuestions: 18,
      testType: 'QUIZ',
      sessionId: 'quiz-session-002',
    },
  ];
};

const generateQuizAttemptSummary = (
  quizId: string
): GetQuizAttemptSummary.Root => {
  const attempts = buildAttempts();
  const coinsEarnedHighest = Math.max(...attempts.map(a => a.coinsEarned));

  return {
    status: 'success',
    responseBody: {
      moduleName: null,
      numberOfAttempts: attempts.length,
      coinsEarnedHighest,
      quizReports: [
        {
          assessmentId: null,
          assessmentType: null,
          moduleId: null,
          topicId: null,
          testType: 'PRE-DEFINED-USER-QUIZ',
          roomId: 'quiz-room-001',
          assessmentName: null,
          assessmentImageUrl: 'https://picsum.photos/200',
          banner: 'https://picsum.photos/200',
          roomName: 'Math Quiz Challenge',
          roomType: 'PRE-DEFINED-USER-QUIZ',
          preDefinedRoomId: quizId,
          reAttemptAllowed: true,
          viewType: 'POST',
          attempts,
        },
      ],
    },
    errorCode: 0,
    errorMessage: null,
  };
};

export const getQuizAttemptSummaryMockHandler = http.get(
  `${env.API_URL}/assessment/v1/users/reports/get-quiz-attempt-summary`,
  async ({ request }): Promise<HttpResponse<GetQuizAttemptSummary.Root>> => {
    await networkDelay();

    const url = new URL(request.url);
    const quizId = url.searchParams.get('quizId') ?? 'YYTKOF';

    const mockResponse = generateQuizAttemptSummary(quizId);

    return HttpResponse.json(mockResponse);
  }
);
